/**
 * Prompt Queue System
 *
 * Rules:
 *  - Only ONE modal per app session (PWA open → close cycle).
 *  - Priority order: push-notifications → biometrics.
 *
 * Push Notifications schedule:
 *  Attempt 1 — first app open from Home Screen after install.
 *  Attempt 2 — first time landing on Chat after successful onboarding
 *               (only if attempt 1 was dismissed).
 *  Attempt 3 — 7 days after last dismissal.
 *  Attempt 4 — 30 days after last dismissal. Final attempt.
 *
 * Biometrics follows the same pattern (to be configured later).
 *
 * Triggers are fired by the consuming code via:
 *   fireEvent('app_first_open')       — PWA opened for the first time after install
 *   fireEvent('onboarding_to_chat')   — user lands on Chat after onboarding completes
 *
 * Time-based triggers (attempt 3 & 4) are evaluated on every fireEvent call.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PromptQueueStorage } from '../helpers/prompt-queue-storage';
import { ENVIRONMENT } from '../env';

const DEV = ENVIRONMENT === 'DEV';
const log = (...args: any[]) => { if (DEV) console.log('[PromptQueue]', ...args); };

// ─── prompt definitions ───

export type PromptId = 'push-notifications' | 'biometrics';

export type PromptTrigger = 'app_first_open' | 'onboarding_to_chat';

type PromptConfig = {
  id: PromptId;
  title: string;
  primaryLabel: string;
  priority: number; // lower = higher priority
};

const PROMPT_CONFIGS: PromptConfig[] = [
  {
    id: 'push-notifications',
    title: 'Stay in rhythm.',
    primaryLabel: 'Enable notifications',
    priority: 1,
  },
  {
    id: 'biometrics',
    title: 'Secure your space.',
    primaryLabel: 'Enable biometrics',
    priority: 2,
  },
];

// Time thresholds for attempt 3 and 4
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

// ─── helpers ───

/**
 * Given a prompt id and a trigger event, decide whether this prompt is eligible
 * to be shown right now.
 */
function isEligible(id: PromptId, trigger: PromptTrigger): boolean {
  // Already enabled → never show
  if (PromptQueueStorage.isEnabled(id)) return false;

  // Exhausted all attempts → never show
  if (PromptQueueStorage.isExhausted(id)) return false;

  // Biometrics: only makes sense after registration — skip on app_first_open
  if (id === 'biometrics' && trigger === 'app_first_open') {
    return false;
  }

  // Push notifications: if already granted, auto-mark as enabled and skip
  if (id === 'push-notifications') {
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      log('push-notifications: browser permission already granted, auto-enabling');
      PromptQueueStorage.setEnabled(id, true);
      return false;
    }
    // NOTE: we still show the modal even if permission is 'denied' —
    // the overlay will guide the user to device Settings to re-enable.
  }

  const attempt = PromptQueueStorage.getAttempt(id); // 0-based (0 = never shown)
  const dismissedAt = PromptQueueStorage.getDismissedAt(id);
  const now = Date.now();

  // Attempt 1: first open after install
  if (attempt === 0 && trigger === 'app_first_open') {
    return true;
  }

  // Attempt 2: landing on chat after onboarding, only if attempt 1 was dismissed
  if (attempt === 1 && trigger === 'onboarding_to_chat') {
    return true;
  }

  // Attempt 3: 7 days after last dismissal (any trigger)
  if (attempt === 2 && dismissedAt && now - dismissedAt >= SEVEN_DAYS_MS) {
    return true;
  }

  // Attempt 4: 30 days after last dismissal (any trigger)
  if (attempt === 3 && dismissedAt && now - dismissedAt >= THIRTY_DAYS_MS) {
    return true;
  }

  return false;
}

// ─── context ───

type PromptQueueContextValue = {
  /** The prompt config currently being shown, or null. */
  activePrompt: PromptConfig | null;

  /** Call when a trigger event occurs. The queue will evaluate eligibility. */
  fireEvent: (trigger: PromptTrigger) => void;

  /** User accepted the prompt (e.g. granted notification permission). */
  handleAccept: () => void;

  /** User dismissed / skipped the prompt. */
  handleDismiss: () => void;
};

const PromptQueueContext = createContext<PromptQueueContextValue | null>(null);

export const PromptQueueProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activePrompt, setActivePrompt] = useState<PromptConfig | null>(null);
  const shownRef = useRef(false); // true once a prompt has been shown this session

  const fireEvent = useCallback((trigger: PromptTrigger) => {
    // Only one prompt per session
    if (shownRef.current) {
      log('fireEvent(%s) — already shown a prompt this session, skipping', trigger);
      return;
    }

    // Also check sessionStorage in case the session flag survived a hot-reload
    if (PromptQueueStorage.hasShownPromptThisSession()) {
      log('fireEvent(%s) — sessionStorage says already shown, skipping', trigger);
      shownRef.current = true;
      return;
    }

    // Evaluate prompts in priority order
    const sorted = [...PROMPT_CONFIGS].sort((a, b) => a.priority - b.priority);

    for (const config of sorted) {
      if (isEligible(config.id, trigger)) {
        log('fireEvent(%s) → showing prompt: %s', trigger, config.id);
        shownRef.current = true;
        PromptQueueStorage.markPromptShownThisSession();
        setActivePrompt(config);
        return;
      }
    }

    log('fireEvent(%s) — no eligible prompts', trigger);
  }, []);

  const handleAccept = useCallback(() => {
    if (!activePrompt) return;
    log('handleAccept(%s)', activePrompt.id);
    PromptQueueStorage.setEnabled(activePrompt.id, true);
    setActivePrompt(null);
  }, [activePrompt]);

  const handleDismiss = useCallback(() => {
    if (!activePrompt) return;
    log('handleDismiss(%s)', activePrompt.id);
    PromptQueueStorage.recordDismissal(activePrompt.id);
    setActivePrompt(null);
  }, [activePrompt]);

  const value = useMemo<PromptQueueContextValue>(
    () => ({ activePrompt, fireEvent, handleAccept, handleDismiss }),
    [activePrompt, fireEvent, handleAccept, handleDismiss],
  );

  return (
    <PromptQueueContext.Provider value={value}>
      {children}
    </PromptQueueContext.Provider>
  );
};

export function usePromptQueue() {
  const ctx = useContext(PromptQueueContext);
  if (!ctx) throw new Error('usePromptQueue must be used within PromptQueueProvider');
  return ctx;
}
